import { useEffect, useState } from "react";
import { Warning, ArrowRight, X } from "@phosphor-icons/react";

/**
 * Banner shown ONLY when the app is loaded on a stale/legacy hostname
 * (e.g. radar-trucks.emergent.host) instead of the canonical production
 * domain. Pushes users to the correct URL with one click while preserving
 * their current path + query string.
 *
 * Background: Emergent keeps preview hostnames resolving indefinitely.
 * Owners who installed the PWA, bookmarked, or followed an old shared
 * link before launch end up here, get logged out (no cookie share with
 * the production domain), and submit "info doesn't load" bug reports.
 *
 * Auto-hides on the canonical hostname, on `localhost`, and on the
 * dev/preview hostname so it never trips during normal development.
 *
 * Dismissable via localStorage flag — re-shows on next page load if they
 * stay on the stale host (intentional; we want them to migrate).
 */
const CANONICAL_HOST = "thetrucktrackr.com";
const DEV_HOSTS = ["localhost", "127.0.0.1", "radar-trucks.preview.emergentagent.com"];
const STORAGE_KEY = "tt:stale-host-dismissed-at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 h — banner returns the next day

function isStaleHost(host) {
  const h = (host || "").toLowerCase();
  if (!h) return false;
  if (h === CANONICAL_HOST || h === `www.${CANONICAL_HOST}`) return false;
  if (DEV_HOSTS.includes(h)) return false;
  // Anything else served from emergent's preview infra that ISN'T the
  // current dev hostname is considered stale (the legacy
  // radar-trucks.emergent.host being the chief offender).
  return h.endsWith(".emergent.host") || h.endsWith(".emergentagent.com");
}

export default function StaleHostBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const host = window.location.hostname;
      if (!isStaleHost(host)) return;
      const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
      if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
      setVisible(true);
    } catch {
      // localStorage unavailable (private mode etc.) — still show the
      // banner; better to over-prompt than to leave them stranded.
      try {
        if (isStaleHost(window.location.hostname)) setVisible(true);
      } catch {
        /* ignore */
      }
    }
  }, []);

  if (!visible) return null;

  const switchUrl = (() => {
    try {
      const { pathname, search, hash } = window.location;
      return `https://${CANONICAL_HOST}${pathname}${search}${hash}`;
    } catch {
      return `https://${CANONICAL_HOST}/`;
    }
  })();

  const onDismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  return (
    <div
      className="sticky top-0 z-[60] w-full bg-amber-500 text-amber-950 shadow-sm"
      role="alert"
      data-testid="stale-host-banner"
    >
      <div className="mx-auto max-w-5xl px-3 py-2 flex items-center gap-3 text-sm">
        <Warning weight="fill" className="h-5 w-5 flex-none" />
        <div className="flex-1 min-w-0 leading-snug">
          <span className="font-bold">You&apos;re on an old TruckTrackr address.</span>{" "}
          <span className="hidden sm:inline">
            Some info won&apos;t load here — switch to the live site to keep using your account.
          </span>
          <span className="sm:hidden">Switch to the live site to keep going.</span>
        </div>
        <a
          href={switchUrl}
          className="flex-none inline-flex items-center gap-1 rounded-full bg-amber-950 text-amber-50 px-3 py-1.5 text-xs font-bold hover:bg-amber-900"
          data-testid="stale-host-switch-btn"
        >
          Switch
          <ArrowRight weight="bold" className="h-3 w-3" />
        </a>
        <button
          type="button"
          onClick={onDismiss}
          className="flex-none h-7 w-7 grid place-items-center rounded-full hover:bg-amber-400/70"
          aria-label="Dismiss for 24 hours"
          data-testid="stale-host-dismiss-btn"
        >
          <X weight="bold" className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
